import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Send, Mail, MessageCircle, Activity, Eye, Loader2, CheckCircle2, Globe, Zap, Play } from 'lucide-react';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Compliance Alerts settings block (iter369).
 *
 * Surfaces three self-serve controls for admins:
 *  1. Toggle for Twilio-WhatsApp fan-out on compliance alerts.
 *  2. "Preview digest" — GET /admin/daily-health-digest and show the
 *     4 counts inline.
 *  3. "Send me a test digest" — POST /admin/daily-health-digest/send
 *     and toast the outcome (or surface the preview HTML when Resend
 *     is not configured yet).
 */
export default function ComplianceAlertsCard() {
  const [settings, setSettings] = useState(null);
  const [savingWa, setSavingWa] = useState(false);
  const [digest, setDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/compliance/settings');
      setSettings(data);
    } catch {
      /* silent - page still works */
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleWa = async (v) => {
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { whatsapp_alerts_enabled: v });
      setSettings(data);
      toast.success(v ? 'WhatsApp compliance alerts enabled' : 'WhatsApp compliance alerts disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update setting');
    } finally {
      setSavingWa(false);
    }
  };

  const toggleDigest = async (v) => {
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { daily_digest_enabled: v });
      setSettings(data);
      toast.success(v ? 'Scheduled daily digest enabled' : 'Scheduled daily digest disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update setting');
    } finally {
      setSavingWa(false);
    }
  };

  const saveTimezone = async (tz) => {
    if (!tz) return;
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { digest_timezone: tz });
      setSettings(data);
      toast.success(`Digest timezone set to ${tz}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save timezone');
    } finally {
      setSavingWa(false);
    }
  };

  const saveHour = async (hr) => {
    const n = Math.max(0, Math.min(23, parseInt(hr, 10) || 0));
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { digest_hour_utc: n });
      setSettings(data);
      toast.success(`Digest hour set to ${n.toString().padStart(2, '0')}:00`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save hour');
    } finally {
      setSavingWa(false);
    }
  };

  const toggleAutoAccept = async (v) => {
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { auto_accept_enabled: v });
      setSettings(data);
      toast.success(v ? 'Auto-accept enabled — high-confidence rows will be linked nightly' : 'Auto-accept disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update setting');
    } finally {
      setSavingWa(false);
    }
  };

  const saveAutoAcceptScore = async (n) => {
    const score = Math.max(80, Math.min(100, parseInt(n, 10) || 95));
    setSavingWa(true);
    try {
      const { data } = await api.put('/compliance/settings', { auto_accept_min_score: score });
      setSettings(data);
      toast.success(`Auto-accept threshold set to ≥ ${score}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save threshold');
    } finally {
      setSavingWa(false);
    }
  };

  const runAutoAcceptNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/banking/quick-link/auto-accept-now', {
        min_score: settings?.auto_accept_min_score || 95,
      });
      toast.success(`Swept ${data.scanned} rows, linked ${data.accepted} (skipped ${(data.skipped || []).length})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run sweep');
    } finally {
      setSending(false);
    }
  };

  const previewDigest = async () => {
    setLoadingDigest(true);
    try {
      const { data } = await api.get('/admin/daily-health-digest');
      setDigest(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load digest');
    } finally {
      setLoadingDigest(false);
    }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/admin/daily-health-digest/send');
      if (data.sent) {
        toast.success(`Digest emailed to ${data.recipients?.length || 0} admin${data.recipients?.length === 1 ? '' : 's'}`);
      } else if (data.skipped_send) {
        toast.info('Preview ready — Resend API key is not configured yet, so nothing was emailed. Showing the preview instead.');
        setDigest(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send digest');
    } finally {
      setSending(false);
    }
  };

  const counts = digest ? [
    { label: 'Compliance reminders due ≤14d', v: digest.reminders_due_14d, href: '/compliance' },
    { label: 'Quick-link backlog', v: digest.quick_link_backlog, href: '/banking-quick-link' },
    { label: 'Unassigned statement txns', v: digest.unassigned_txns, href: '/banking-quick-link' },
    { label: 'Expiring employee docs ≤14d', v: digest.expiring_docs_14d, href: '/employees' },
  ] : [];

  return (
    <Card className="border-border mt-4" data-testid="compliance-alerts-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Activity size={18} className="text-violet-600" />
          Compliance Alerts &amp; Daily Health Digest
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Compliance reminders fire every morning. Beyond the in-app
          bell and email, you can route high-priority alerts to
          WhatsApp and preview / email a daily health snapshot.
        </p>

        {/* WA toggle */}
        <div className="flex items-center justify-between p-4 border rounded-xl bg-violet-50/40">
          <div className="flex gap-3 items-start min-w-0">
            <MessageCircle size={18} className="text-violet-600 mt-0.5" />
            <div>
              <div className="font-medium text-sm">WhatsApp fan-out</div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                When enabled, every compliance alert also pings the
                WhatsApp recipient configured on the WhatsApp tab.
                Requires a valid Twilio account — silently skipped
                otherwise.
              </p>
            </div>
          </div>
          <Switch
            checked={!!settings?.whatsapp_alerts_enabled}
            disabled={savingWa || !settings}
            onCheckedChange={toggleWa}
            data-testid="wa-alerts-toggle"
          />
        </div>

        {/* Digest controls */}
        <div className="p-4 border rounded-xl space-y-3" data-testid="digest-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                <Mail size={16} className="text-sky-600" />
                Daily Health Digest
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                A one-email summary: compliance due, banking backlog,
                unassigned statement txns, expiring HR docs. Preview
                below or send a test to yourself + other admins.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl text-xs"
                onClick={previewDigest}
                disabled={loadingDigest}
                data-testid="digest-preview-btn"
              >
                {loadingDigest ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Eye size={13} className="mr-1" />}
                Preview
              </Button>
              <Button
                size="sm"
                className="rounded-xl text-xs bg-sky-600 hover:bg-sky-700 text-white"
                onClick={sendDigest}
                disabled={sending}
                data-testid="digest-send-btn"
              >
                {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}
                Send me a test digest
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 border-t mt-2">
            <div className="flex gap-3 items-center">
              <Activity size={14} className="text-violet-500" />
              <div>
                <Label className="text-sm" htmlFor="digest-schedule-toggle">Send this digest daily</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Runs inside the compliance scheduler; only sends when there's something to report.
                </p>
              </div>
            </div>
            <Switch
              id="digest-schedule-toggle"
              checked={!!settings?.daily_digest_enabled}
              disabled={savingWa || !settings}
              onCheckedChange={toggleDigest}
              data-testid="digest-schedule-toggle"
            />
          </div>

          {settings?.daily_digest_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2" data-testid="digest-tz-grid">
              <div>
                <Label className="text-xs flex items-center gap-1 mb-1 text-muted-foreground">
                  <Globe size={11} /> Timezone (IANA)
                </Label>
                <Input
                  defaultValue={settings?.digest_timezone || 'UTC'}
                  placeholder="e.g. Asia/Riyadh"
                  className="h-9 text-sm"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== settings?.digest_timezone) saveTimezone(v);
                  }}
                  data-testid="digest-tz-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Matches common zones: UTC · Asia/Riyadh · Europe/London · America/New_York
                </p>
              </div>
              <div>
                <Label className="text-xs mb-1 text-muted-foreground">Send at (local hour, 0–23)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  defaultValue={settings?.digest_hour_utc ?? 6}
                  className="h-9 text-sm"
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!Number.isNaN(n) && n !== settings?.digest_hour_utc) saveHour(n);
                  }}
                  data-testid="digest-hour-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Interpreted in the timezone above.
                </p>
              </div>
            </div>
          )}

          {digest && (
            <div className="space-y-2" data-testid="digest-results">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 size={14} className="text-emerald-500" />
                Digest for {digest.date} · {digest.recipients?.length || 0} recipient{digest.recipients?.length === 1 ? '' : 's'}
                {digest.skipped_send && <Badge variant="outline" className="text-[10px] ml-2">Preview only — RESEND_API_KEY missing</Badge>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {counts.map((c) => (
                  <div key={c.label} className="flex items-center justify-between p-2 border rounded-lg bg-white">
                    <span className="text-xs text-muted-foreground">{c.label}</span>
                    <span className={`text-sm font-semibold ${c.v > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{c.v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* iter376 Auto-accept high-confidence Quick-link rows */}
        <div className="p-4 border rounded-xl space-y-3 bg-emerald-50/30" data-testid="auto-accept-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex gap-3">
              <Zap size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-sm">Auto-accept high-confidence Quick-link matches</div>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                  Each night, the scheduler sweeps your inbox and links rows that score above the threshold below — no clicks needed. Lower-confidence rows stay for you to review.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings?.auto_accept_enabled}
              disabled={savingWa || !settings}
              onCheckedChange={toggleAutoAccept}
              data-testid="auto-accept-toggle"
            />
          </div>
          {settings?.auto_accept_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 pt-1">
              <div>
                <Label className="text-xs mb-1 text-muted-foreground">Minimum score (80–100)</Label>
                <Input
                  type="number"
                  min={80}
                  max={100}
                  defaultValue={settings?.auto_accept_min_score ?? 95}
                  className="h-9 text-sm"
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!Number.isNaN(n) && n !== settings?.auto_accept_min_score) saveAutoAcceptScore(n);
                  }}
                  data-testid="auto-accept-score-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  ≥ 95 = "auto-pilot" — IBAN exact / IBAN tail / account # only. Lower the threshold to scoop in name-based matches too.
                </p>
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl text-xs h-9"
                  onClick={runAutoAcceptNow}
                  disabled={sending}
                  data-testid="auto-accept-run-now-btn"
                >
                  {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Play size={13} className="mr-1" />}
                  Run now
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
